import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

export type AuthShellMode =
  | 'chooser'
  | 'login'
  | 'signup'
  | 'confirm'
  | 'ton-unlock'
  | 'ton-recovery'
  | 'passkey'

type AuthShellContextValue = {
  open: boolean
  mode: AuthShellMode
  openAuth: (mode?: AuthShellMode) => void
  closeAuth: () => void
}

const Ctx = createContext<AuthShellContextValue | null>(null)

export function AuthShellProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<AuthShellMode>('chooser')
  const openAuth = useCallback((next: AuthShellMode = 'chooser') => {
    setMode(next)
    setOpen(true)
  }, [])
  const closeAuth = useCallback(() => setOpen(false), [])
  const value = useMemo(
    () => ({ open, mode, openAuth, closeAuth }),
    [closeAuth, mode, open, openAuth]
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuthShell(): AuthShellContextValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuthShell must be used inside <AuthShellProvider>')
  return ctx
}

export function useOptionalAuthShell(): AuthShellContextValue | null {
  return useContext(Ctx)
}
